import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, Button, Spin } from 'antd';
import {
  CameraOutlined,
  CheckCircleFilled,
  LoadingOutlined,
  WarningFilled,
  ReloadOutlined,
  BulbOutlined,
  UserOutlined,
  ForwardOutlined,
} from '@ant-design/icons';
import { BrowserInfo } from '../hooks/useBrowserDetection';
import HelpAccordions from '../components/HelpAccordions';
import CameraBlockedModal from '../../modals/CameraBlockedModal';
import '../DeviceSetupWizard.scss';

// Detection thresholds (same as useCameraObstructionDetection for consistency)
const DARK_THRESHOLD = 30;           // Average brightness below this = too dark (blocked by hand/object)
const BRIGHT_THRESHOLD = 240;        // Average brightness above this = too bright (paper covering)
const VARIANCE_THRESHOLD = 100;      // Color variance below this = uniform/covered
const CONSECUTIVE_BLOCKED_FRAMES = 5; // Frames needed to confirm camera is blocked

type CameraState = 'initializing' | 'permission-needed' | 'scanning' | 'detected' | 'verified' | 'blocked' | 'error';

interface CameraSetupStepProps {
  onComplete: () => void;
  onInvalidate: () => void; // Called when camera becomes blocked after verification
  onStreamReady: (stream: MediaStream) => void;
  isCompleted: boolean;
  browserInfo: BrowserInfo;
}

const CameraSetupStep: React.FC<CameraSetupStepProps> = ({
  onComplete,
  onInvalidate,
  onStreamReady,
  isCompleted,
  browserInfo,
}) => {
  const [cameraState, setCameraState] = useState<CameraState>('initializing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [faceDetected, setFaceDetected] = useState(false);
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [scanningTime, setScanningTime] = useState(0);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [obstructionReason, setObstructionReason] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scanningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const verificationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onInvalidateRef = useRef(onInvalidate);
  const goodFrameCountRef = useRef(0);
  const blockedFrameCountRef = useRef(0);
  const cameraStateRef = useRef<CameraState>(cameraState);
  const hasShownBlockedModalRef = useRef(false);
  
  // Keep refs updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);
  
  useEffect(() => {
    onInvalidateRef.current = onInvalidate;
  }, [onInvalidate]);
  
  useEffect(() => {
    cameraStateRef.current = cameraState;
  }, [cameraState]);

  // Show skip button after 5 seconds if still scanning, or if blocked (user may want to continue anyway)
  const SKIP_THRESHOLD_SECONDS = 5;
  const canSkipVerification = scanningTime >= SKIP_THRESHOLD_SECONDS && (cameraState === 'scanning' || cameraState === 'blocked');

  const handleSkipVerification = useCallback(() => {
    hasCompletedRef.current = true;
    blockedFrameCountRef.current = 0; // Reset blocked counter
    hasShownBlockedModalRef.current = false; // Reset modal flag
    
    if (scanningTimerRef.current) {
      clearInterval(scanningTimerRef.current);
    }
    setCameraState('verified');
    setFaceDetected(true);
    onCompleteRef.current();
  }, []);

  const handleBlockedModalAcknowledge = useCallback(() => {
    setShowBlockedModal(false);
    // Don't reset hasShownBlockedModalRef here - it will be reset when camera starts working again
    // This prevents the modal from showing repeatedly while camera is still blocked
  }, []);

  const startCamera = useCallback(async () => {
    setCameraState('initializing');
    setErrorMessage('');
    setVideoReady(false);
    setScanningTime(0);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
        audio: true, // Include audio for proctoring
      });

      streamRef.current = stream;
      onStreamReady(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready before playing
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => {
                setVideoReady(true);
                setCameraState('scanning');
              })
              .catch((err) => {
                console.error('Video play error:', err);
                // Try to continue even if autoplay fails
                setVideoReady(true);
                setCameraState('scanning');
              });
          }
        };
      }
    } catch (error: any) {
      console.error('Camera access error:', error);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setCameraState('permission-needed');
        setErrorMessage('Camera access was denied. Please allow camera access to continue.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setCameraState('error');
        setErrorMessage('No camera found. Please connect a camera and try again.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        setCameraState('error');
        setErrorMessage('Camera is in use by another application. Please close other apps using the camera.');
      } else {
        setCameraState('error');
        setErrorMessage('Failed to access camera. Please check your camera settings.');
      }
    }
  }, [onStreamReady]);

  // Camera verification - checks if camera is showing actual content (not blocked)
  // CONTINUOUS monitoring - keeps checking even after verification to detect if camera gets blocked
  // Uses same detection logic as useCameraObstructionDetection for consistency
  useEffect(() => {
    if (!videoReady) return;

    const video = videoRef.current;
    if (!video) return;

    // Create canvas for sampling
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const REQUIRED_GOOD_FRAMES = 5;
    const REQUIRED_BLOCKED_FRAMES = 10; // Frames needed to confirm camera is blocked after verification
    goodFrameCountRef.current = 0;
    blockedFrameCountRef.current = 0;

    // Start scanning timer for skip button
    scanningTimerRef.current = setInterval(() => {
      setScanningTime((prev) => prev + 1);
    }, 1000);

    // Check camera content periodically - CONTINUOUS monitoring
    verificationIntervalRef.current = setInterval(() => {
      // Ensure video has content
      if (video.readyState < video.HAVE_CURRENT_DATA || video.videoWidth === 0) {
        return;
      }

      // Sample video frame (use smaller size for performance, same as useCameraObstructionDetection)
      const sampleWidth = 160;
      const sampleHeight = 120;
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;
      
      try {
        ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);
        const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
        const pixels = imageData.data;

        // Calculate statistics (same as useCameraObstructionDetection)
        let totalBrightness = 0;
        const brightnessValues: number[] = [];
        const pixelCount = pixels.length / 4;

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          // Calculate perceived brightness (human eye weighted)
          const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
          totalBrightness += brightness;
          brightnessValues.push(brightness);
        }

        const avgBrightness = totalBrightness / pixelCount;

        // Calculate variance (how much pixels differ from average)
        let varianceSum = 0;
        for (const brightness of brightnessValues) {
          varianceSum += Math.pow(brightness - avgBrightness, 2);
        }
        const variance = varianceSum / pixelCount;

        // Detect obstruction using same thresholds as useCameraObstructionDetection
        let detectedReason: string | null = null;

        if (avgBrightness < DARK_THRESHOLD) {
          detectedReason = "dark";
        } else if (avgBrightness > BRIGHT_THRESHOLD && variance < VARIANCE_THRESHOLD) {
          detectedReason = "bright";
        } else if (variance < VARIANCE_THRESHOLD) {
          detectedReason = "uniform";
        }

        if (!detectedReason) {
          // Camera is showing valid content
          blockedFrameCountRef.current = 0; // Reset blocked counter
          hasShownBlockedModalRef.current = false; // Reset modal flag when camera is working
          
          if (!hasCompletedRef.current) {
            // Initial verification - not yet completed
            goodFrameCountRef.current++;
            setFaceDetected(true);
            setCameraState('detected');
            setVerificationProgress(Math.min(100, (goodFrameCountRef.current / REQUIRED_GOOD_FRAMES) * 100));

            if (goodFrameCountRef.current >= REQUIRED_GOOD_FRAMES) {
              hasCompletedRef.current = true;
              if (scanningTimerRef.current) clearInterval(scanningTimerRef.current);
              setCameraState('verified');
              onCompleteRef.current();
            }
          } else {
            // Already verified - camera is still working, ensure state is correct
            if (cameraStateRef.current === 'blocked') {
              // Camera was blocked but now working again
              setCameraState('verified');
              setFaceDetected(true);
              onCompleteRef.current(); // Re-notify completion
            }
          }
        } else {
          // Camera appears blocked/covered
          goodFrameCountRef.current = 0;
          blockedFrameCountRef.current++;
          
          if (blockedFrameCountRef.current >= CONSECUTIVE_BLOCKED_FRAMES) {
            // Confirmed blocked - show modal if not already shown
            if (!hasShownBlockedModalRef.current) {
              hasShownBlockedModalRef.current = true;
              setObstructionReason(detectedReason);
              setShowBlockedModal(true);
            }
            
            if (hasCompletedRef.current) {
              // Camera was verified but now appears blocked
              if (blockedFrameCountRef.current >= REQUIRED_BLOCKED_FRAMES) {
                setFaceDetected(false);
                setCameraState('blocked');
                setVerificationProgress(0);
                hasCompletedRef.current = false; // Reset completion
                onInvalidateRef.current(); // Notify parent that step is no longer valid
              }
            } else {
              // Initial verification - camera is blocked
              setFaceDetected(false);
              setCameraState('blocked');
              setVerificationProgress(0);
            }
          }
        }
      } catch (err) {
        console.error('Camera verification error:', err);
      }
    }, 200); // Check every 200ms

    return () => {
      if (verificationIntervalRef.current) {
        clearInterval(verificationIntervalRef.current);
        verificationIntervalRef.current = null;
      }
      if (scanningTimerRef.current) {
        clearInterval(scanningTimerRef.current);
        scanningTimerRef.current = null;
      }
    };
  }, [videoReady]); // Only depend on videoReady - cameraState is checked inside interval

  // Initialize camera on mount
  useEffect(() => {
    startCamera();

    return () => {
      // Clear all intervals
      if (scanningTimerRef.current) {
        clearInterval(scanningTimerRef.current);
        scanningTimerRef.current = null;
      }
      if (verificationIntervalRef.current) {
        clearInterval(verificationIntervalRef.current);
        verificationIntervalRef.current = null;
      }
      // Note: Stream is NOT stopped here - parent (DeviceSetupWizard) handles it
      // because the stream needs to be passed to the assessment context
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusBadge = () => {
    switch (cameraState) {
      case 'initializing':
        return (
          <div className="status-badge scanning">
            <LoadingOutlined spin /> Starting camera...
          </div>
        );
      case 'scanning':
        return (
          <div className="status-badge scanning">
            <LoadingOutlined spin /> Checking camera feed...
          </div>
        );
      case 'detected':
        return (
          <div className="status-badge detected">
            <UserOutlined /> Camera working - Verifying...
          </div>
        );
      case 'verified':
        return (
          <div className="status-badge verified">
            <CheckCircleFilled /> Camera ready!
          </div>
        );
      case 'blocked':
        return (
          <div className="status-badge error">
            <WarningFilled /> Camera appears blocked or covered
          </div>
        );
      case 'error':
      case 'permission-needed':
        return (
          <div className="status-badge error">
            <WarningFilled /> {errorMessage}
          </div>
        );
      default:
        return null;
    }
  };

  const getStatusItems = () => [
    {
      icon: cameraState === 'initializing' ? 'loading' : (streamRef.current ? 'success' : 'error'),
      text: 'Camera connected',
    },
    {
      icon: cameraState === 'blocked' ? 'error' : (faceDetected ? 'success' : (cameraState === 'scanning' ? 'loading' : 'warning')),
      text: cameraState === 'blocked' ? 'Camera blocked or covered' : 'Video feed active',
    },
    {
      icon: cameraState === 'verified' ? 'success' : (cameraState === 'blocked' ? 'error' : 'warning'),
      text: cameraState === 'verified' ? 'Ready to proceed' : (cameraState === 'blocked' ? 'Unblock camera to continue' : 'Verifying...'),
    },
  ];

  return (
    <div className="setup-step camera-setup-step">
      {/* Header */}
      <div className="step-header">
        <Typography.Title level={5} className="step-title">
          <CameraOutlined /> Camera Setup
        </Typography.Title>
        <Typography.Text className="step-description" type="secondary">
          Make sure your camera shows a clear view of your face
        </Typography.Text>
      </div>

      {/* Main Content */}
      <div className="step-main">
        {/* Camera Preview */}
        <div className="step-content">
          <div className="camera-preview-container">
            <div className="camera-preview-wrapper">
              {/* Always render video element so it's ready for the stream */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-feed"
                style={{
                  display: cameraState === 'error' || cameraState === 'permission-needed' ? 'none' : 'block',
                }}
              />

              {/* Loading Overlay */}
              {cameraState === 'initializing' && (
                <div className="camera-placeholder" style={{ position: 'absolute', inset: 0 }}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                  <Typography.Text style={{ color: '#fff', marginTop: 16 }}>
                    Starting camera...
                  </Typography.Text>
                </div>
              )}

              {/* Error State */}
              {(cameraState === 'error' || cameraState === 'permission-needed') && (
                <div className="camera-placeholder">
                  <WarningFilled className="placeholder-icon" style={{ color: '#ff4d4f' }} />
                  <Typography.Text style={{ color: '#fff', textAlign: 'center', padding: '0 24px' }}>
                    {errorMessage}
                  </Typography.Text>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={startCamera}
                    style={{ marginTop: 16 }}
                  >
                    Try Again
                  </Button>
                </div>
              )}

              {/* Scan Overlay - show when not in error state */}
              {cameraState !== 'error' && cameraState !== 'permission-needed' && cameraState !== 'initializing' && (
                <div className="scan-overlay">
                  {/* Corner Brackets */}
                  <div className={`corner-brackets ${cameraState}`}>
                    <div className="bracket-bottom-left" />
                    <div className="bracket-bottom-right" />
                  </div>

                  {/* Scan Line */}
                  {cameraState === 'scanning' && <div className="scan-line" />}
                </div>
              )}

              {/* Status Badge */}
              {getStatusBadge()}
            </div>

            {/* Progress Bar for verification */}
            {(cameraState === 'detected' || cameraState === 'scanning') && (
              <div style={{ width: '100%', maxWidth: 640 }}>
                <div
                  style={{
                    height: 4,
                    background: '#f0f0f0',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${verificationProgress}%`,
                      background: faceDetected ? '#52c41a' : '#1890ff',
                      transition: 'width 0.1s ease',
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Status Checklist */}
          <div className="status-checklist">
            {getStatusItems().map((item, index) => (
              <div key={index} className="status-item">
                <span className={`status-icon ${item.icon}`}>
                  {item.icon === 'loading' ? (
                    <LoadingOutlined spin />
                  ) : item.icon === 'success' ? (
                    <CheckCircleFilled />
                  ) : (
                    <WarningFilled />
                  )}
                </span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>

          {/* Tips */}
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Typography.Text type="secondary">
              <BulbOutlined /> Good lighting
            </Typography.Text>
            <Typography.Text type="secondary">
              <BulbOutlined /> Face the camera directly
            </Typography.Text>
            <Typography.Text type="secondary">
              <BulbOutlined /> Keep your face in frame
            </Typography.Text>
          </div>

          {/* Skip button - shows after 5 seconds if verification is stuck or camera appears blocked */}
          {canSkipVerification && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                {cameraState === 'blocked' 
                  ? 'If your camera is working and you can see yourself, click below to continue.'
                  : 'Verification is taking longer than expected. If you can see yourself, click below.'}
              </Typography.Text>
              <Button
                type="link"
                icon={<ForwardOutlined />}
                onClick={handleSkipVerification}
              >
                I can see myself - Continue
              </Button>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="step-help">
          <HelpAccordions type="camera" browserInfo={browserInfo} />
        </div>
      </div>

      {/* Camera Blocked Modal - notify user during setup (no violation logging) */}
      <CameraBlockedModal
        open={showBlockedModal}
        onAcknowledge={handleBlockedModalAcknowledge}
        reason={obstructionReason}
      />
    </div>
  );
};

export default CameraSetupStep;
